import { useWorkspaceConnections } from "@web-app/api";
import {
  AccordionV2,
  Avatar,
  OrderCard,
  RouteCard,
  Skeleton,
} from "@web-app/components";
import { focusOnOrder } from "@web-app/pages/common/DispatchMap/mapFocusUtils";
import useDispatchStore from "@web-app/store/dispatchStore";
import { BaseApp, Order, Route, User } from "@web-app/types";

import { getContentCountTexts } from "../../utils";

import useConnectionsView from "./useConnectionsView";

interface ConnectionsViewProps {
  appsMap: Map<string, BaseApp>;
  assigneesMap: Map<string, User>;
  autoAssignSearchingIds: Set<string>;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  isLoadingOrders: boolean;
  onSelectExternalProvider: (
    entity: Order | Route | null,
    provider: string,
    type: string,
  ) => void;
  onShare: (orderId: string, connectedWorkspaceId: string) => void;
  routedOrdersMap: Map<string, Order>;
  routes: Route[];
  searchTerm: string;
  unroutedOrders: Order[];
}

export default function ConnectionsView({
  appsMap,
  assigneesMap,
  autoAssignSearchingIds,
  isAppsLoading,
  isAssigneesLoading,
  unroutedOrders,
  routedOrdersMap,
  onShare,
  searchTerm,
  routes,
  isLoadingOrders,
  onSelectExternalProvider,
}: ConnectionsViewProps) {
  const { isWorkspaceConnectionsLoading, ordersGroupedByWorkspace } =
    useConnectionsView({
      routes,
      unroutedOrders,
    });

  const currentUser = useDispatchStore((state) => state.user);
  const { data: workspaceConnections } = useWorkspaceConnections();

  if (isWorkspaceConnectionsLoading || isLoadingOrders) {
    return (
      <div className="flex flex-col px-4">
        <div className="flex flex-col gap-2 mt-2">
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton
            height="h-[40px] lg:h-[61px]"
            width="w-full"
            className=" bg-gradient-to-b from-gray-200 !bg-white to-gray-50"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col relative z-10 overflow-y-auto h-full">
      {ordersGroupedByWorkspace.map((workspace) => {
        const contentCountText = getContentCountTexts(
          workspace.routes,
          workspace.orders,
        );

        return (
          <AccordionV2 key={workspace.id} isBorderless>
            <AccordionV2.Label>
              <div className="flex flex-col items-start pl-4">
                <span className="text-gray-700 font-normal text-xs lg:text-sm flex gap-2 items-center">
                  <Avatar image={workspace.avatarUrl ?? undefined} size={8} />
                  <span className="flex flex-col items-start">
                    {workspace.name}
                    <span className="text-gray-500 font-normal text-[10px] lg:text-xs">
                      {contentCountText}
                    </span>
                  </span>
                </span>
              </div>
            </AccordionV2.Label>
            <AccordionV2.Content>
              <div className="flex flex-col mt-1 gap-2 mb-2 px-4">
                {workspace.routes.map((route) => (
                  <RouteCard
                    key={route.id}
                    route={route}
                    routedOrdersMap={routedOrdersMap}
                    searchTerm={searchTerm}
                    onSelectExternalProvider={onSelectExternalProvider}
                    assigneesMap={assigneesMap}
                    isAssigneesLoading={isAssigneesLoading}
                    isAutoAssignSearching={autoAssignSearchingIds.has(route.id)}
                    appsMap={appsMap}
                    isAppsLoading={isAppsLoading}
                  />
                ))}
                {workspace.orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    app={order.appId ? appsMap.get(order.appId) : null}
                    isAppsLoading={isAppsLoading}
                    assignee={
                      order.assigneeId
                        ? assigneesMap.get(order.assigneeId)
                        : null
                    }
                    isAssigneesLoading={isAssigneesLoading}
                    isAutoAssignSearching={autoAssignSearchingIds.has(order.id)}
                    searchTerm={searchTerm}
                    workspaceConnections={workspaceConnections || []}
                    onViewDetails={() => {
                      useDispatchStore.setState({
                        focusedOrderId: order.id,
                        mapFocusedOrderId: order.id,
                      });

                      setTimeout(() => {
                        const { dispatchMapRef } = useDispatchStore.getState();
                        if (dispatchMapRef?.current) {
                          focusOnOrder({
                            mapRef: dispatchMapRef.current,
                            order,
                          });
                        }
                      }, 0);
                    }}
                    onShare={
                      currentUser?.currentWorkspaceId === order?.workspaceId
                        ? (connectedWorkspaceId) =>
                            onShare(order.id, connectedWorkspaceId)
                        : undefined
                    }
                    onSelectExternalProvider={onSelectExternalProvider}
                  />
                ))}
              </div>
            </AccordionV2.Content>
          </AccordionV2>
        );
      })}
    </div>
  );
}
